/**
 * The canonical client-side error type every service call rejects with
 * (PX-W6.1).
 *
 * The PX backend emits TWO error envelope shapes, and the client must map
 * both to an i18n key:
 *
 *  1. `infrastructure/kernel/ErrorBodies.json(code, key)` →
 *     `{"code": <int>, "error": "<message key>", "message": "<EN text>"}`
 *     (the filters + the canonical shape).
 *  2. `infrastructure/http/JsonResults.errorJson(code, key)` →
 *     `{"code": <int>, "message": <MessageResolver.resolve(key)>}` — NO
 *     `error` field. This is the DOMINANT path (every controller's
 *     `.exceptionally(JsonResults::resultFromThrowable)`), so keying on
 *     `error` alone left the wrong-password 401 unmappable.
 *
 * How a key is recovered from shape 2: `MessageResolver.resolve` returns the
 * key ITSELF when it is not in the server bundle. The auth keys the FE cares
 * about (`auth.credentials.invalid`, `auth.account.locked`, …) are not in
 * `conf/messages`, so their `message` field IS the key verbatim — recognised
 * by the dot-cased-key shape. Keys the server DOES translate
 * (`organisation.slug.taken`, …) arrive as EN sentences; no key can be
 * recovered from a sentence, so those degrade to the generic fallback (never
 * raw JSON, never an EN sentence inside a NL UI — I-10).
 */

import { isAxiosError } from 'axios'

/**
 * A failed backend call. `backendKey` carries the derived i18n message key
 * (e.g. `organisation.slug.taken`); `status` is `0` when the request never
 * reached the server (network / timeout).
 */
export interface ApiError {
  status: number
  backendKey?: string
  message: string
  /** Per-field validation sentences, when the envelope carries `details`. */
  details?: Record<string, string[]>
}

/** Narrow an unknown value to `Record<string, string[]>` for validation details. */
function isValidationDetails(value: unknown): value is Record<string, string[]> {
  if (typeof value !== 'object' || value === null) return false
  return Object.values(value as Record<string, unknown>).every(
    (v) => Array.isArray(v) && v.every((item) => typeof item === 'string')
  )
}

/**
 * Whether a `message` value is really an untranslated MESSAGE KEY come back
 * verbatim (shape 2): dot-cased, lowercase/digit/hyphen segments, at least one
 * dot — the charset of every key the backend throws. Human sentences (spaces,
 * capitals, terminal punctuation) never match.
 */
const MESSAGE_KEY_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*(?:\.[a-z0-9]+(?:-[a-z0-9]+)*)+$/

const isMessageKey = (value: string): boolean => MESSAGE_KEY_PATTERN.test(value)

/**
 * Map a failed response's status + parsed body onto {@link ApiError}, reading
 * BOTH px envelope shapes (see the file header). The one field mapping the
 * axios path ({@link toApiError}) and the POST-SSE path
 * (`ApiClient.postEventStream`, whose refusals arrive before any frame) share,
 * so the two can never diverge.
 *
 * @param status          - The HTTP status (the envelope's int `code` equals it).
 * @param body            - The parsed body; anything that is not an object reads as `{}`.
 * @param fallbackMessage - The message when the body carries no string `message`.
 */
export function apiErrorFromBody(status: number, body: unknown, fallbackMessage: string): ApiError {
  const data = (typeof body === 'object' && body !== null ? body : {}) as Record<string, unknown>
  const errorField = typeof data['error'] === 'string' ? data['error'] : undefined
  const messageField = typeof data['message'] === 'string' ? data['message'] : fallbackMessage
  let backendKey = errorField
  if (backendKey === undefined && isMessageKey(messageField)) {
    backendKey = messageField
  }
  return {
    status,
    backendKey,
    message: messageField,
    details: isValidationDetails(data['details']) ? data['details'] : undefined
  }
}

/**
 * Map any throwable (an AxiosError from the interceptor, a stranger) into the
 * canonical {@link ApiError}, deriving `backendKey` from BOTH envelope shapes.
 *
 * The envelope's int `code` equals the HTTP status, so `response.status` is
 * preferred — it is present even when the body is not a canonical shape.
 */
export function toApiError(error: unknown): ApiError {
  if (isAxiosError(error)) {
    const response = error.response
    if (response) {
      return apiErrorFromBody(response.status, response.data, error.message || 'An unexpected error occurred')
    }
    // Network error / timeout — no response at all.
    return { status: 0, backendKey: 'network', message: error.message || 'Network error' }
  }
  if (error instanceof Error) {
    return { status: 0, backendKey: 'network', message: error.message || 'Network error' }
  }
  return { status: 0, backendKey: 'network', message: 'Network error' }
}

/**
 * The shape every feature slice stores a failed thunk under: the server's
 * status, the i18n-mappable key, and the raw message.
 *
 * It differs from {@link ApiError} in one field name only (`key` vs
 * `backendKey`) because a slice's rejection payload is its own contract — but
 * the panels take an `ApiError`, so every page used to spell the conversion
 * inline and half of them hardcoded the status while doing it. See
 * {@link asApiError}.
 */
export interface KeyedError {
  status: number
  key?: string
  message: string
}

/**
 * A slice's stored error as the panels want it.
 *
 * ⚠ Use this at every `<ErrorPanel error={…}>` call site instead of building
 * the object by hand. W6.6's first cut wrote `{ status: 500, … }` and
 * `{ status: 0, … }` literals at nine call sites, which discarded the server's
 * real status and made a 403 unbranchable — AC-6.2 was unimplemented on two
 * surfaces as a direct result (merged review C2). A helper cannot be
 * mis-transcribed the way nine inline object literals were.
 */
export const asApiError = (error: KeyedError): ApiError => ({
  status: error.status,
  backendKey: error.key,
  message: error.message
})

/** Whether a stored error is the server refusing (403), not a failure. */
export const isForbidden = (error: KeyedError | ApiError | null | undefined): boolean =>
  error !== null && error !== undefined && error.status === 403

/**
 * Whether a thrown value is already an {@link ApiError} (services throw
 * hand-built ones for client-side refusals that have no HTTP response, e.g.
 * the 2FA-required login answer).
 */
export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'status' in value &&
    'message' in value &&
    typeof (value as { status: unknown }).status === 'number' &&
    typeof (value as { message: unknown }).message === 'string'
  )
}
